import { Link } from '@tanstack/react-router';
import { useQueries } from '@tanstack/react-query';
import { canCorrect, canReplace, type EFile, type EFileDetail } from '@tabservice/tabfile-client';
import { FilePlus2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/feedback';
import { FilingStatusPill } from './status-pill';
import { filingQuery } from '@/lib/queries';

/**
 * What can be filed against this filing right now. Tab Service decides it from the
 * IRS status and what has already been sent, so the buttons are never a guess
 * the app has made on its own.
 */
export function FilingActions({ filing }: { filing: Pick<EFile, 'id' | 'actions'> }) {
  const correct = canCorrect(filing);
  const replace = canReplace(filing);
  if (!correct && !replace) return null;

  return (
    <div data-tour="e-file-actions" className="flex flex-wrap items-center gap-3">
      {correct ? (
        <Button asChild>
          <Link to="/e-files/$efileId/correct" params={{ efileId: filing.id }}>
            <FilePlus2 className="h-4 w-4" aria-hidden="true" />
            Submit corrections
          </Link>
        </Button>
      ) : null}
      {replace ? (
        <Button asChild>
          <Link to="/e-files/$efileId/replace" params={{ efileId: filing.id }}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Submit replacement
          </Link>
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Where this filing sits in its family: what it amends above, and what has been
 * filed against it below. Both directions matter when the IRS is asked about a
 * return, because only one e-file in the chain is the one that counts.
 */
export function FilingLineage({ filing }: { filing: EFileDetail }) {
  const children = filing.childEfileIds ?? [];
  const results = useQueries({
    queries: children.map((childId) => ({ ...filingQuery(childId), staleTime: 60_000 })),
  });

  if (!filing.parentEfileId && children.length === 0) return null;

  const parentWord = filing.kind === 'replacement' ? 'Replacement of e-file' : 'Correction of e-file';

  return (
    <div className="flex flex-col gap-4">
      {filing.parentEfileId ? (
        <p className="text-base text-muted-foreground">
          {parentWord}{' '}
          <Link
            to="/e-files/$efileId"
            params={{ efileId: filing.parentEfileId }}
            className="text-link hover:underline"
          >
            {filing.parentEfileId}
          </Link>
          .
        </p>
      ) : null}

      {children.length > 0 ? (
        <div>
          <p className="text-base font-medium text-gray-3">Follow-up e-files</p>
          <ul className="mt-2 flex flex-col gap-2">
            {children.map((childId, index) => {
              const child = results[index]?.data;
              return (
                <li key={childId} className="flex flex-wrap items-center gap-3">
                  <Link
                    to="/e-files/$efileId"
                    params={{ efileId: childId }}
                    className="text-base text-link hover:underline"
                  >
                    {childId}
                  </Link>
                  {child ? (
                    <>
                      <span className="text-base text-muted-foreground">
                        {child.kind === 'replacement' ? 'Replacement' : 'Correction'}, {child.recordCount} records
                      </span>
                      <FilingStatusPill status={child.status} />
                    </>
                  ) : (
                    <Skeleton className="h-5 w-40" />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
